import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { BerthApiService } from '@bcmApiServices/berth.api-service';
import { DurationUnit, getDurationFromDates } from '@shared/functions/get-duration-from-dates';
import { DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';
import { Product } from '@shared/models/product';
import { DEFAULT_BERTH_RESERVATION_UNIT } from '@modules/bcm/settings/default-units/default-units.component';

import { BookingV2BerthOccupancy } from '../dtos/booking-v2.dtos';
import {
    BerthProductContext,
    managedBerthProductKeys,
    reconcileBerthProductServices,
} from '../utils/booking-v2-berth-products';
import { BookingV2Facade } from './booking-v2.facade';

// The identifying inputs of one reconcile pass: which berths take part, for which
// window, under which Beziehung and against which stay-duration unit. Deliberately
// excludes the resolved products and prices, so loading a catalog or writing a
// price back cannot re-trigger it.
interface BerthProductContextKey {
    relationId: number | null;
    // Stringified: DEFAULT_BERTH_RESERVATION_UNIT.NONE is `null`, which makes the
    // enum's inferred type awkward to carry around.
    timeUnitType: string;
    berths: { berthId: number; from: number | null; to: number | null; openEnd: boolean }[];
}

// Seeds a berth's Produkte into the booking's Leistungen (#6734): as soon as a
// berth joins the booking, its products become billable service lines — filtered by
// the berth product's optional Beziehung condition, quantified by the stay duration
// for day/overnight units, and carrying the Belegungszeitraum as Leistungszeitraum.
// From there they are ordinary lines: editable in the Leistungen tab, priced by
// BookingV2ServicePricingService, listed in the Rechnungsversand step and billed by
// mapPositions (which links them back to their berth assignment).
//
// Provided at the dialog level so it shares the dialog's BookingV2Facade instance
// and survives the berth/services steps being unmounted.
@Injectable()
export class BookingV2BerthProductService {

    private readonly facade = inject(BookingV2Facade);
    private readonly berthApi = inject(BerthApiService);

    // Berth catalogs, keyed by berth id. A berth is fetched at most once per dialog;
    // a berth that is removed and re-picked reuses the cached list.
    private readonly catalog = signal<ReadonlyMap<number, readonly Product[]>>(new Map());

    // Berth ids whose catalog could not be loaded — surfaced as a warning, because a
    // failed request would otherwise be indistinguishable from a berth that simply
    // has no Produkte, and the booking would silently save without them.
    private readonly failedBerthIds = signal<ReadonlySet<number>>(new Set());

    readonly loadFailed = computed(() => this.failedBerthIds().size > 0);

    // Lines the user deleted in the Leistungen tab. Without this the next reconcile
    // pass would simply re-seed them ("missing" is indistinguishable from "removed").
    // Cleared per berth when that berth leaves the booking, so deliberately picking
    // it again starts from a clean slate.
    private readonly suppressed = new Set<string>();

    // Keys the previous pass left in the booking — the baseline for spotting a
    // line that vanished without the reconcile having removed it.
    private lastManagedKeys = new Set<string>();

    // Berths this dialog may seed. An occupancy carrying an `assignmentId` came from
    // the saved booking: its positions already exist and are loaded as ordinary
    // services, so seeding it again would duplicate every line. Re-picking the same
    // berth creates a fresh occupancy without an id — that one IS seeded.
    private readonly seedableOccupancies = computed<readonly BookingV2BerthOccupancy[]>(() =>
        this.facade.berthOccupancies().filter(o => o.berth != null && o.assignmentId == null),
    );

    private readonly contextKey = computed<BerthProductContextKey>(() => {
        const range = this.facade.dateRange();
        return {
            relationId: this.facade.selectedRelation()?.id ?? null,
            timeUnitType: String(this.facade.defaultTimeUnit()?.type ?? ''),
            berths: this.seedableOccupancies().map(o => ({
                berthId: o.berth.id,
                from: (o.from ?? range.from)?.getTime() ?? null,
                to: o.openEnd ? null : ((o.to ?? range.to)?.getTime() ?? null),
                openEnd: o.openEnd,
            })),
        };
    });

    constructor() {
        // debounceTime(0) coalesces a synchronous burst (picking a berth and its
        // window in one go) into ONE pass; distinctUntilChanged drops no-ops.
        toObservable(this.contextKey)
            .pipe(
                debounceTime(0),
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                switchMap(() => this.loadMissingCatalogs()),
                tap(() => this.reconcile()),
                takeUntilDestroyed(),
            )
            .subscribe();
    }

    private loadMissingCatalogs(): Observable<unknown> {
        const missing = this.seedableOccupancies()
            .map(o => o.berth.id)
            .filter(id => !this.catalog().has(id));

        if (!missing.length) {
            return of(null);
        }

        const requests = missing.map(berthId =>
            this.berthApi.getProducts(berthId).pipe(
                map(products => ({ berthId, products })),
                catchError(() => {
                    this.failedBerthIds.update(prev => new Set(prev).add(berthId));
                    return of<{ berthId: number; products: Product[] } | null>(null);
                }),
            ),
        );

        // forkJoin over an array variable widens to Observable<unknown> in this rxjs
        // version; the cast is sound — `requests` is typed above.
        return forkJoin(requests).pipe(
            tap(raw => {
                const results = raw as ({ berthId: number; products: Product[] } | null)[];
                const next = new Map(this.catalog());
                for (const result of results) {
                    if (result) {
                        next.set(result.berthId, result.products);
                        // A later attempt succeeded — drop the stale warning.
                        if (this.failedBerthIds().has(result.berthId)) {
                            this.failedBerthIds.update(prev => {
                                const rest = new Set(prev);
                                rest.delete(result.berthId);
                                return rest;
                            });
                        }
                    }
                }
                this.catalog.set(next);
            }),
        );
    }

    private reconcile(): void {
        const contexts = this.buildContexts();
        const relationId = this.facade.selectedRelation()?.id ?? null;

        this.forgetSuppressionOfAbsentBerths(contexts);
        this.recordUserRemovals(contexts);

        this.facade.services.update(services => {
            const next = reconcileBerthProductServices(services, contexts, relationId, this.suppressed);
            return next === services ? services : [...next];
        });

        this.lastManagedKeys = managedBerthProductKeys(this.facade.services());
    }

    // A managed line that the previous pass produced but that is no longer in the
    // booking, while its berth is still present with a loaded catalog, can only have
    // been deleted by the user — remember it so the next pass does not re-seed it.
    private recordUserRemovals(contexts: readonly BerthProductContext[]): void {
        if (!this.lastManagedKeys.size) {
            return;
        }
        const settled = new Set(
            contexts.filter(c => c.products != null).map(c => c.berthId),
        );
        const current = managedBerthProductKeys(this.facade.services());
        for (const key of this.lastManagedKeys) {
            if (current.has(key)) {
                continue;
            }
            const berthId = Number(key.split(':')[0]);
            if (settled.has(berthId)) {
                this.suppressed.add(key);
            }
        }
    }

    // Re-picking a berth is a deliberate act: it starts over rather than silently
    // honouring deletions from an earlier visit.
    private forgetSuppressionOfAbsentBerths(contexts: readonly BerthProductContext[]): void {
        const present = new Set(contexts.map(c => c.berthId));
        for (const key of [...this.suppressed]) {
            if (!present.has(Number(key.split(':')[0]))) {
                this.suppressed.delete(key);
            }
        }
    }

    private buildContexts(): BerthProductContext[] {
        const range = this.facade.dateRange();
        const timeUnit = this.facade.defaultTimeUnit();
        const catalog = this.catalog();

        return this.seedableOccupancies().map(o => {
            const from = o.from ?? range.from;
            const to = o.openEnd ? null : (o.to ?? range.to);
            return {
                berthId: o.berth.id,
                from,
                to,
                openEnd: o.openEnd,
                durationOfStay: this.durationOfStay(from, to, timeUnit),
                // Not yet fetched → null = pending (existing lines are kept); an
                // empty array is a berth that genuinely has no products.
                products: catalog.get(o.berth.id) ?? null,
            };
        });
    }

    // Same derivation as the legacy dialog (booking-dialog-positions._getDurationOfStay):
    // the tenant's configured unit decides whether a stay counts nights or days.
    private durationOfStay(
        from: Date | null,
        to: Date | null,
        timeUnit: DefaultBerthReservationTimeUnit | null,
    ): number {
        if (!from || !to) {
            return 0;
        }
        const unit = timeUnit?.type === DEFAULT_BERTH_RESERVATION_UNIT.OVERNIGHT_STAY
            ? DurationUnit.Overnights
            : DurationUnit.Days;
        return getDurationFromDates(from, to, unit, timeUnit as DefaultBerthReservationTimeUnit) ?? 0;
    }
}
